import React from "react";
import { useTinyHost } from "@tiny/host-contract";
import { useForm } from "react-hook-form";
import { CombinedCondition } from "@tiny/api";
import { useApi } from "@tiny/api";
import { useConfirm, useMessages } from "@tiny/components";
import {
  useGetSegmentBySegmentIdQuery,
  useGetSegmentConditionsInfoQuery,
  usePutSegmentBySegmentIdMutation,
} from "@tiny/api";

import { EditSegmentProps, SegmentConditionsInfo } from "./EditSegment.types";
import {
  addInitialConditions,
  removeEmptyConditions,
} from "./EditSegment.helpers";
import { isPredefinedSegment } from "../../components/SegmentList/SegmentList.helpers";
import { isEqual } from "lodash";

export function useEditSegment(props: EditSegmentProps) {
  const api = useApi();
  const { autoSaveDelay = 2000 } = props;

  const { segmentIdParam, segmentNameParam } = useEditSegmentParams(
    props.segmentId
  );

  const showConfirm = useConfirm();

  const { navigate } = useTinyHost();

  const { showWarningMessage, closeMessage, showErrorMessage } = useMessages();

  const form = useForm<CombinedCondition>({
    mode: "onChange",
  });

  const {
    reset,
    formState: { isValid },
  } = form;

  const [segmentName, setSegmentName] = React.useState(segmentNameParam || "");

  const [nameEditing, setNameEditing] = React.useState(false);

  const [summaryOpen, setSummaryOpen] = React.useState(false);

  const messageIdRef = React.useRef<string | undefined>();

  const { data: segmentDetails, isLoading: isSegmentLoading } =
    useGetSegmentBySegmentIdQuery(segmentIdParam!, {
      onSuccess(data) {
        setSegmentName(data.name!);
        reset(addInitialConditions(data?.filter));
      },
      enabled: !!segmentIdParam,
    });

  const { data, isLoading: isSegmentConditionsInfoLoading } =
    useGetSegmentConditionsInfoQuery({
      cacheTime: Infinity,
    });

  const segmentConditionsInfo = data as unknown as SegmentConditionsInfo;

  const { mutateAsync: putSegmentMutateAsync } =
    usePutSegmentBySegmentIdMutation();

  const openSummary = React.useCallback(() => {
    setSummaryOpen(true);
  }, []);

  const closeSummary = React.useCallback(() => {
    setSummaryOpen(false);
  }, []);

  const wasModified = React.useCallback(() => {
    const originalValues = segmentDetails?.filter;
    const currentValues = form.getValues();

    return isEqual(
      removeEmptyConditions(originalValues),
      removeEmptyConditions(currentValues)
    );
  }, [segmentDetails, form]);

  const clearMessage = React.useCallback(async () => {
    if (messageIdRef.current) {
      await closeMessage(messageIdRef.current);
      messageIdRef.current = undefined;
    }
  }, [closeMessage]);

  const save = React.useCallback(async () => {
    const values = form.getValues();
    const filteredValues = removeEmptyConditions(values);
    await putSegmentMutateAsync({
      segmentId: segmentIdParam!,
      data: {
        name: segmentName,
        filter: filteredValues,
      },
    });
  }, [form, putSegmentMutateAsync, segmentIdParam, segmentName]);

  const editName = React.useCallback(() => {
    setNameEditing(true);
  }, []);

  const cancelEditName = React.useCallback(() => {
    setNameEditing(false);
  }, []);

  const saveName = React.useCallback(
    async (value: string) => {
      clearMessage();

      if (value === segmentName || !segmentIdParam) {
        setSegmentName(value);
        setNameEditing(false);
        return;
      }

      try {
        const { data: nameIsUsed } = await api.getSegmentCheckExists({
          name: value,
        });
        if (nameIsUsed) {
          messageIdRef.current = showWarningMessage(
            `Audience with name "${value}" already exists!`
          );
        } else {
          await putSegmentMutateAsync({
            segmentId: segmentIdParam,
            data: { name: value },
          });
          setSegmentName(value);
          setNameEditing(false);
        }
      } catch (e) {
        showErrorMessage("Something wrong happened");
      }
    },
    [
      clearMessage,
      putSegmentMutateAsync,
      segmentIdParam,
      segmentName,
      showErrorMessage,
      showWarningMessage,
    ]
  );

  const gotoView = React.useCallback(async () => {
    if (isValid) {
      try {
        await save();
        navigate.to("/main/audience?tab=custom_segments");
      } catch (error) {
        showErrorMessage("Failed to save segment");
      }
    }
    navigate.to("/main/audience?tab=custom_segments");
  }, [navigate, isValid, save, showErrorMessage]);

  const goBack = React.useCallback(() => {
    if (!wasModified()) {
      if (isValid) {
        showConfirm({
          title: "You may have unsaved changes.",
          message: "Are you sure you want to leave the page?",
          actions: [
            {
              label: "Leave without saving",
              onClick: () => {
                navigate.to("/main/audience?tab=custom_segments");
              },
            },
            {
              label: "Save",
              primary: true,
              onClick: () => gotoView(),
            },
          ],
        });
      } else {
        showConfirm({
          title: "Form is invalid.",
          message:
            "You have some errors in the form. Are you sure you want to leave without fixing them?",
          actions: [
            {
              label: "Leave without saving anyway",
              onClick: () => {
                navigate.to("/main/audience?tab=custom_segments");
              },
            },
            {
              label: "Stay",
              primary: true,
            },
          ],
        });
      }
    } else {
      navigate.to("/main/audience?tab=custom_segments");
    }
  }, [gotoView, navigate, wasModified, isValid]);

  const segmentId = segmentIdParam;
  const editDisabled = isPredefinedSegment(segmentDetails?.segmentType);
  const editDisabledTooltip = "You can't edit predefined segments.";
  const isLoading = isSegmentLoading || isSegmentConditionsInfoLoading;
  const previewDisabled = !isValid;

  return {
    autoSaveDelay,
    cancelEditName,
    closeSummary,
    editDisabled,
    editDisabledTooltip,
    editName,
    form,
    gotoView,
    isLoading,
    isSegmentLoading,
    nameEditing,
    openSummary,
    previewDisabled,
    save,
    saveName,
    segmentConditionsInfo,
    segmentDetails,
    segmentId,
    segmentName,
    summaryOpen,
    goBack,
  };
}

function useEditSegmentParams(segmentIdProp?: string) {
  const { navigate } = useTinyHost();
  const location = {
    state: (navigate.getLocationState?.() ?? {}) as { segmentName?: string },
  };

  const setSegmentIdParam = (id: string) => {
    navigate.replace(`/main/audience/edit-segments/${id}`);
  };

  const segmentIdParam = segmentIdProp;
  const segmentNameParam = location?.state?.segmentName;

  return {
    segmentIdParam,
    segmentNameParam,
    setSegmentIdParam,
  };
}
